import { ScanResultDto, ScantoolUserInfoDto, TokenDto } from '../dtos';
import { useQuery } from '@tanstack/react-query';
import lang from '../i18n/lang';
import { ModuleTypeEnum } from './module-type.enum';

// Express/NestJS standard error shape: { message: string, textDE?: string }
type ApiErrorResponse = { textDE?: string; message?: string };

export class ScanApiError extends Error {
  constructor(code: string, public readonly apiMessage: string) {
    super(code);
  }
}

export async function getInfo(token: string): Promise<ScantoolUserInfoDto> {
  const response = await fetch(`/api/v1/info`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  return await response.json();
}

export const useGetInfo = (token: string) => {
  return useQuery({
    queryKey: ['scanToolInfo'],
    queryFn: () => getInfo(token),
  });
};

export async function scanToolLogin(pw: string): Promise<TokenDto> {
  const response = await fetch(`/api/v1/login`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ password: pw }),
  });
  if (response.status >= 500) {
    throw new Error('INTERNAL_SERVER_ERROR');
  }
  if (response.status >= 400) {
    throw new Error('INVALID_PIN');
  }
  return await response.json();
}

export async function scanTicket(
  scanSubModuleId: string,
  dn: string,
  token: string,
  action?: string,
): Promise<ScanResultDto> {
  const response = await fetch(`/api/v1/scan`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      scanSubModuleId: scanSubModuleId,
      metadata: action && {
        action: action,
      },
      delegateNumber: dn,
    }),
  });
  if (response.status >= 500) {
    const message = (await response.json()) as ApiErrorResponse;
    throw new ScanApiError(
      'INTERNAL_SERVER_ERROR',
      message.textDE || message.message || lang('scan.error.generic'),
    );
  }
  if (response.status === 401) {
    const message = (await response.json()) as ApiErrorResponse;
    throw new ScanApiError(
      'INVALID_TOKEN',
      message.textDE || message.message || lang('scan.error.session.expired'),
    );
  }
  if (response.status === 400) {
    const message = (await response.json()) as ApiErrorResponse;
    throw new ScanApiError(
      'UNAUTHORIZED',
      message.textDE || message.message || lang('scan.error.unauthorized'),
    );
  }
  if (response.status === 404) {
    throw new ScanApiError(
      'INVALID_INPUT',
      lang('scan.error.invalid.delegate', [dn]),
    );
  }
  if (response.status === 403) {
    const message = (await response.json()) as ApiErrorResponse;
    throw new ScanApiError(
      'SCAN_PERSON_FORBIDDEN',
      message.textDE || message.message || lang('scan.error.forbidden'),
    );
  }
  return await response.json();
}
